'use client';

// Building blocks every sidebar filter group repeats: the chip combobox
// trigger, its popover panel, the removable chip, the option check mark and
// the submit-on-Enter key handler. Each renders exactly the markup the groups
// used to inline, so a new picker looks like the old ones by construction.
import type { ReactNode } from 'react';
import { Check, ChevronDown, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PopoverContent, PopoverTrigger } from '@/components/ui/popover';

/** The outlined combobox button that opens a picker, showing its chips or a
 *  placeholder (`children`) and the chevron. Must sit inside a `Popover`. */
export function ComboboxTrigger({
  disabled,
  children,
}: {
  disabled?: boolean;
  children: ReactNode;
}) {
  return (
    <PopoverTrigger asChild>
      <Button
        variant="outline"
        role="combobox"
        disabled={disabled}
        className="w-full min-h-11 lg:min-h-9 h-auto font-normal text-left justify-start py-1.5 px-3 rounded-lg relative"
      >
        <div className="flex flex-wrap gap-1 w-full">
          {children}
          <ChevronDown className="absolute right-2 top-2 h-4 w-4 shrink-0 opacity-50" />
        </div>
      </Button>
    </PopoverTrigger>
  );
}

/** The picker panel, as wide as its trigger. */
export function ComboboxContent({ children }: { children: ReactNode }) {
  return (
    <PopoverContent
      className="w-[var(--radix-popover-trigger-width)] p-0"
      align="start"
    >
      {children}
    </PopoverContent>
  );
}

/** One selected value inside a trigger, with the × that removes it. The
 *  click stops there so it does not also open the picker. */
export function FilterChip({
  onRemove,
  children,
}: {
  onRemove: () => void;
  children: ReactNode;
}) {
  return (
    <Badge
      variant="secondary"
      className="text-xs font-normal gap-1 pr-1 border border-foreground"
    >
      {children}
      <X
        size={12}
        className="cursor-pointer hover:bg-muted rounded-sm"
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation();
          onRemove();
        }}
      />
    </Badge>
  );
}

/** The check mark in front of a picker option; invisible, not absent, when
 *  unselected so the labels stay aligned. */
export function OptionCheck({ selected }: { selected: boolean }) {
  return (
    <Check
      className={cn('mr-2 h-4 w-4', selected ? 'opacity-100' : 'opacity-0')}
    />
  );
}

/** onKeyDown for a free-text filter input: Enter runs the search instead of
 *  submitting a form. */
export function submitOnEnter(handleFormSubmit: () => void) {
  return (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleFormSubmit();
    }
  };
}
